import { z } from "zod";

import { CrmDealCreationResultSchema } from "./crmDealCreation";
import { FieldMappingAiConfigSchema, FieldMappingSourceType } from "./hubspot";

export type { AiTestRequest, AiTestResponse, FieldMappingAiConfig } from "./hubspot";
export { AiTestResponseSchema } from "./hubspot";

// Entity types for field mappings
export enum PipedriveEntityType {
	LEAD = "LEAD",
	PERSON = "PERSON",
	ORGANIZATION = "ORGANIZATION",
	DEAL = "DEAL",
}

// Re-export the shared source type enum so pipedrive consumers don't need to import from the hubspot schema.
export { FieldMappingSourceType };

// Reserved Pipedrive deal field keys that are owned by the deal-config endpoint or set
// automatically by the sync's relationship-linking logic. The API rejects mappings that
// target these keys with a 400, so we hide them from the deal target-field picker.
export const PIPEDRIVE_DEAL_RESERVED_FIELD_KEYS: readonly string[] = [
	"title",
	"pipeline_id",
	"stage_id",
	"user_id",
	"expected_close_date",
	"currency",
	"person_id",
	"org_id",
];

// Pipedrive CRM field from API
export const PipedriveFieldSchema = z.object({
	name: z.string(),
	label: z.string(),
	type: z.string(),
	isCustom: z.boolean().optional(),
	description: z.string().optional(),
	options: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
	isRequired: z.boolean().optional(),
	isReadOnly: z.boolean().optional(),
	isCalculated: z.boolean().optional(),
	isMulti: z.boolean().optional(),
});

export type PipedriveField = z.infer<typeof PipedriveFieldSchema>;

// Source field from our system
export const PipedriveSourceFieldSchema = z.object({
	name: z.string(),
	label: z.string(),
	type: z.string().optional(),
	category: z.string().optional(),
});

export type PipedriveSourceField = z.infer<typeof PipedriveSourceFieldSchema>;

// Field mapping
export const PipedriveFieldMappingSchema = z.object({
	id: z.number(),
	entityType: z.nativeEnum(PipedriveEntityType),
	sourceField: z.string(),
	sourceType: z.nativeEnum(FieldMappingSourceType),
	pipedriveField: z.string(),
	pipedriveFieldLabel: z.string().optional(),
	isCustomField: z.boolean().optional(),
	isActive: z.boolean(),
	aiConfig: FieldMappingAiConfigSchema.optional(),
	createdAt: z.string().optional(),
	updatedAt: z.string().optional(),
});

export type PipedriveFieldMapping = z.infer<typeof PipedriveFieldMappingSchema>;

// Create field mapping DTO
export const CreatePipedriveFieldMappingSchema = z.object({
	entityType: z.nativeEnum(PipedriveEntityType),
	sourceField: z.string().min(1),
	sourceType: z.nativeEnum(FieldMappingSourceType).optional(),
	pipedriveField: z.string().min(1),
	pipedriveFieldLabel: z.string().optional(),
	isCustomField: z.boolean().optional(),
	isActive: z.boolean().optional(),
	aiConfig: FieldMappingAiConfigSchema.optional(),
});

export type CreatePipedriveFieldMapping = z.infer<typeof CreatePipedriveFieldMappingSchema>;

// Update field mapping DTO
export const UpdatePipedriveFieldMappingSchema = z.object({
	sourceField: z.string().optional(),
	sourceType: z.nativeEnum(FieldMappingSourceType).optional(),
	pipedriveField: z.string().optional(),
	pipedriveFieldLabel: z.string().optional(),
	isCustomField: z.boolean().optional(),
	isActive: z.boolean().optional(),
	aiConfig: FieldMappingAiConfigSchema.optional(),
});

export type UpdatePipedriveFieldMapping = z.infer<typeof UpdatePipedriveFieldMappingSchema>;

// Bulk create mappings
export const BulkCreatePipedriveFieldMappingsSchema = z.object({
	mappings: z.array(CreatePipedriveFieldMappingSchema),
});

export type BulkCreatePipedriveFieldMappings = z.infer<
	typeof BulkCreatePipedriveFieldMappingsSchema
>;

// API Responses
export const PipedriveFieldsResponseSchema = z.object({
	fields: z.array(PipedriveFieldSchema),
	entityType: z.nativeEnum(PipedriveEntityType).optional(),
});

export type PipedriveFieldsResponse = z.infer<typeof PipedriveFieldsResponseSchema>;

export const PipedriveSourceFieldsResponseSchema = z.object({
	personFields: z.array(PipedriveSourceFieldSchema),
	organizationFields: z.array(PipedriveSourceFieldSchema),
	dealFields: z.array(PipedriveSourceFieldSchema).optional(),
	leadFields: z.array(PipedriveSourceFieldSchema).optional(),
});

export type PipedriveSourceFieldsResponse = z.infer<typeof PipedriveSourceFieldsResponseSchema>;

export const PipedriveFieldMappingsResponseSchema = z.array(PipedriveFieldMappingSchema);

export type PipedriveFieldMappingsResponse = z.infer<typeof PipedriveFieldMappingsResponseSchema>;

// Sync Trigger Types
export enum PipedriveSyncTrigger {
	DISABLED = "DISABLED",
	ON_CAMPAIGN_START = "ON_CAMPAIGN_START",
	ON_REPLY = "ON_REPLY",
}

export enum PipedriveSyncTriggerWithInherit {
	INHERIT = "INHERIT",
	DISABLED = "DISABLED",
	ON_CAMPAIGN_START = "ON_CAMPAIGN_START",
	ON_REPLY = "ON_REPLY",
}

// Company Sync Trigger Response
export const PipedriveCompanySyncTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(PipedriveSyncTrigger),
});

export type PipedriveCompanySyncTriggerResponse = z.infer<
	typeof PipedriveCompanySyncTriggerResponseSchema
>;

// List Sync Trigger Response
export const PipedriveListSyncTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(PipedriveSyncTriggerWithInherit),
	effectiveTrigger: z.nativeEnum(PipedriveSyncTrigger),
	isInherited: z.boolean(),
});

export type PipedriveListSyncTriggerResponse = z.infer<
	typeof PipedriveListSyncTriggerResponseSchema
>;

// Update Sync Trigger DTOs
export const UpdatePipedriveCompanySyncTriggerSchema = z.object({
	trigger: z.nativeEnum(PipedriveSyncTrigger),
});

export type UpdatePipedriveCompanySyncTrigger = z.infer<
	typeof UpdatePipedriveCompanySyncTriggerSchema
>;

export const UpdatePipedriveListSyncTriggerSchema = z.object({
	trigger: z.nativeEnum(PipedriveSyncTriggerWithInherit),
});

export type UpdatePipedriveListSyncTrigger = z.infer<typeof UpdatePipedriveListSyncTriggerSchema>;

// Sync Log
export const PipedriveSyncLogSchema = z.object({
	id: z.number(),
	entityType: z.nativeEnum(PipedriveEntityType),
	status: z.enum(["PENDING", "IN_PROGRESS", "SUCCESS", "PARTIAL", "FAILED"]),
	recordsSynced: z.number(),
	recordsFailed: z.number(),
	/** One entry per failed record: the message Pipedrive or the sync gave, and the prospect. */
	errors: z
		.array(z.object({ error: z.string().optional(), prospectId: z.number().optional() }))
		.nullish(),
	startedAt: z.string().optional(),
	createdAt: z.string(),
	completedAt: z.string().optional(),
});

export type PipedriveSyncLog = z.infer<typeof PipedriveSyncLogSchema>;

export const PipedriveSyncLogsResponseSchema = z.array(PipedriveSyncLogSchema);

export type PipedriveSyncLogsResponse = z.infer<typeof PipedriveSyncLogsResponseSchema>;

// Deal Creation Trigger Types
export enum PipedriveDealCreationTrigger {
	DISABLED = "DISABLED",
	ON_RESPONSE = "ON_RESPONSE",
	ON_POSITIVE_RESPONSE = "ON_POSITIVE_RESPONSE",
}

// Company Deal Creation Trigger Response (includes createAsLead)
export const PipedriveCompanyDealCreationTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(PipedriveDealCreationTrigger),
	createAsLead: z.boolean(),
});

export type PipedriveCompanyDealCreationTriggerResponse = z.infer<
	typeof PipedriveCompanyDealCreationTriggerResponseSchema
>;

// Update Company Deal Creation Trigger DTO
export const UpdatePipedriveCompanyDealCreationTriggerSchema = z.object({
	trigger: z.nativeEnum(PipedriveDealCreationTrigger),
	createAsLead: z.boolean().optional(),
});

export type UpdatePipedriveCompanyDealCreationTrigger = z.infer<
	typeof UpdatePipedriveCompanyDealCreationTriggerSchema
>;

// Pipeline Stage
export const PipedrivePipelineStageSchema = z.object({
	id: z.string(),
	label: z.string(),
	displayOrder: z.number().optional(),
});

export type PipedrivePipelineStage = z.infer<typeof PipedrivePipelineStageSchema>;

// Pipeline
export const PipedrivePipelineSchema = z.object({
	id: z.string(),
	label: z.string(),
	stages: z.array(PipedrivePipelineStageSchema),
});

export type PipedrivePipeline = z.infer<typeof PipedrivePipelineSchema>;

export const PipedrivePipelinesResponseSchema = z.object({
	pipelines: z.array(PipedrivePipelineSchema),
});

export type PipedrivePipelinesResponse = z.infer<typeof PipedrivePipelinesResponseSchema>;

// Owner
export const PipedriveOwnerSchema = z.object({
	id: z.string(),
	name: z.string(),
	email: z.string(),
});

export type PipedriveOwner = z.infer<typeof PipedriveOwnerSchema>;

export const PipedriveOwnersResponseSchema = z.object({
	owners: z.array(PipedriveOwnerSchema),
});

export type PipedriveOwnersResponse = z.infer<typeof PipedriveOwnersResponseSchema>;

// Deal Configuration (with defaultCurrency, no auto-create campaigns)
export const PipedriveDealConfigSchema = z.object({
	defaultPipelineId: z.string().optional().nullable(),
	defaultStageId: z.string().optional().nullable(),
	dealNameTemplate: z.string().optional().nullable(),
	expectedCloseDateOffset: z.number().optional().nullable(),
	defaultOwnerId: z.string().optional().nullable(),
	defaultCurrency: z.string().optional().nullable(),
});

export type PipedriveDealConfig = z.infer<typeof PipedriveDealConfigSchema>;

export const PipedriveDealConfigResponseSchema = PipedriveDealConfigSchema;

export type PipedriveDealConfigResponse = z.infer<typeof PipedriveDealConfigResponseSchema>;

// Lead Configuration (Pipedrive Leads - no pipeline/stage; response-driven)
export const PipedriveLeadConfigSchema = z.object({
	defaultCurrency: z.string().optional().nullable(),
	defaultValue: z.number().optional().nullable(),
	defaultLabelIds: z.array(z.string()).optional().nullable(),
	defaultOwnerId: z.string().optional().nullable(),
	expectedCloseDateOffset: z.number().optional().nullable(),
});

export type PipedriveLeadConfig = z.infer<typeof PipedriveLeadConfigSchema>;

export const PipedriveLeadConfigResponseSchema = PipedriveLeadConfigSchema;

export type PipedriveLeadConfigResponse = z.infer<typeof PipedriveLeadConfigResponseSchema>;

// User Mapping (Signalist user → Pipedrive owner)
export const PipedriveUserMappingSchema = z.object({
	id: z.number(),
	userId: z.number(),
	pipedriveOwnerId: z.string(),
	pipedriveOwnerName: z.string().optional(),
	userFirstName: z.string().optional(),
	userLastName: z.string().optional(),
	userEmail: z.string().optional(),
});

export type PipedriveUserMapping = z.infer<typeof PipedriveUserMappingSchema>;

export const PipedriveUserMappingsResponseSchema = z.array(PipedriveUserMappingSchema);

export type PipedriveUserMappingsResponse = z.infer<typeof PipedriveUserMappingsResponseSchema>;

// Create a single user mapping
export const CreatePipedriveUserMappingSchema = z.object({
	userId: z.number(),
	pipedriveOwnerId: z.string(),
	pipedriveOwnerName: z.string().optional(),
});

export type CreatePipedriveUserMapping = z.infer<typeof CreatePipedriveUserMappingSchema>;

// Create Pipedrive Deal/Lead
export const CreatePipedriveDealRequestSchema = z.object({
	prospectId: z.number(),
	dealName: z.string().optional(),
	createAsLead: z.boolean().optional(),
	pipeline: z.string().optional(),
	dealStage: z.string().optional(),
	dealOwnerId: z.string().optional(),
	closeDate: z.string().optional(),
});

export type CreatePipedriveDealRequest = z.infer<typeof CreatePipedriveDealRequestSchema>;

export const CreatePipedriveDealResponseSchema = CrmDealCreationResultSchema;

export type CreatePipedriveDealResponse = z.infer<typeof CreatePipedriveDealResponseSchema>;

// Conversation Sync
export const PipedriveConversationSyncResponseSchema = z.object({
	enabled: z.boolean(),
});

export type PipedriveConversationSyncResponse = z.infer<
	typeof PipedriveConversationSyncResponseSchema
>;

export const UpdatePipedriveConversationSyncSchema = z.object({
	enabled: z.boolean(),
});

export type UpdatePipedriveConversationSync = z.infer<typeof UpdatePipedriveConversationSyncSchema>;

// OAuth
export const PipedriveOAuthConnectResponseSchema = z.object({
	url: z.string(),
});

export type PipedriveOAuthConnectResponse = z.infer<typeof PipedriveOAuthConnectResponseSchema>;

export const PipedriveOAuthStatusResponseSchema = z.object({
	connected: z.boolean(),
	companyDomain: z.string().optional(),
	apiDomain: z.string().optional(),
	pipedriveUserId: z.string().optional(),
	pipedriveCompanyId: z.string().optional(),
	tokenExpiry: z.string().optional(),
	/** Pipedrive refused to refresh the stored sign-in; the company must reconnect (PREC-4353). */
	needsReauthorization: z.boolean().optional(),
});

export type PipedriveOAuthStatusResponse = z.infer<typeof PipedriveOAuthStatusResponseSchema>;

export const PipedriveOAuthDisconnectResponseSchema = z.object({
	success: z.boolean(),
});

export type PipedriveOAuthDisconnectResponse = z.infer<
	typeof PipedriveOAuthDisconnectResponseSchema
>;

// Manual Resync
export const PipedriveResyncResponseSchema = z.object({
	queued: z.boolean(),
	queuedCount: z.number(),
	companyId: z.number(),
	triggeredAt: z.string(),
});

export type PipedriveResyncResponse = z.infer<typeof PipedriveResyncResponseSchema>;
